import { useCallback, useEffect, useMemo, useState } from 'react';
import { Bell, Check, Trash2, AlertCircle, Inbox } from 'lucide-react';
import { format, isToday, isYesterday } from 'date-fns';
import { supabase } from '../services/supabase';
import { useAuthStore } from '../store/authStore';

interface NotificationView {
  id: string;
  title: string;
  body: string;
  type: string | null;
  read: boolean;
  dismissed: boolean;
  created_at: string;
  user_id: string | null;
}

// EL TOPE DE LA PÁGINA. Se piden TOPE + 1 filas y se pintan TOPE: si vuelve la
// de más, es que hay más, y la página lo DICE. Es la lección del círculo de
// Comunicaciones (#725): una lista que se acaba sola a los 100 no distingue «no
// hay más» de «no te lo estoy enseñando».
//
// Aquí NO es un riesgo futuro, es de hoy: esta página ya trae las descartadas
// —el checkbox de abajo las pinta—, y medido en producción hay 7 personas con
// más de 100 filas (máximo 120). Un admin y seis directores llevan tiempo
// viendo una bandeja recortada sin que nada se lo dijera.
const TOPE = 100;

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (isToday(d)) return `Hoy ${format(d, 'HH:mm')}`;
  if (isYesterday(d)) return `Ayer ${format(d, 'HH:mm')}`;
  return format(d, "d 'de' MMM 'de' yyyy HH:mm");
}

// Página §15 admin: listado de notificaciones del usuario logueado. La consulta
// sigue aceptando las filas sin dueño (`user_id IS NULL`), pero desde la mig 330
// ya no se escriben: el anuncio del club va con una fila por persona. Las dos
// globales que quedan de 2026 están descartadas a propósito. Marca leídas al montar y
// permite descartar individualmente vía RPC `dismiss_notification`
// (migración 054). El badge unread vive en el Header (componente
// padre); no se comparte estado — ambos consultan la BD.
export default function NotificacionesPage() {
  const profile = useAuthStore((s) => s.profile);
  const [items, setItems] = useState<NotificationView[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDismissed, setShowDismissed] = useState(false);
  const [error, setError] = useState('');
  // §129 · ⚠️ Una carga que falla NO es una bandeja vacía. Con la lectura en
  // 403 esta página pintaba el error y, debajo, «No hay notificaciones
  // activas»: la frase tranquilizadora encima de una lectura que no se pudo
  // hacer. Lo mismo que se arregló en la bandeja del entrenador (§128), aquí
  // estaba desde el primer día.
  const [cargaFallida, setCargaFallida] = useState(false);
  // Vinieron más filas de las que caben. Ver `TOPE`.
  const [truncado, setTruncado] = useState(false);

  const load = useCallback(async () => {
    if (!profile) return;
    const { data, error: qErr } = await supabase
      .from('notifications')
      .select('id, title, body, type, read, dismissed, created_at, user_id')
      .or(`user_id.eq.${profile.id},user_id.is.null`)
      .order('created_at', { ascending: false })
      .limit(TOPE + 1);
    if (qErr) {
      setError(qErr.message);
      setCargaFallida(true);
      // Una lectura que falló no afirma nada sobre el tope: si se dejara el
      // valor anterior, un refresco en 403 seguiría diciendo «hay más».
      setTruncado(false);
      setLoading(false);
      return;
    }
    setCargaFallida(false);
    // ⚠️ El tope se mide sobre la PÁGINA CRUDA, antes del filtro de descartadas
    // (`visible`). Medirlo sobre lo ya filtrado diría «no hay más» justo cuando
    // el filtro se ha comido la prueba de que sí.
    const pagina = (data ?? []) as NotificationView[];
    setTruncado(pagina.length > TOPE);
    setItems(pagina.slice(0, TOPE) as NotificationView[]);
    setLoading(false);
  }, [profile]);

  useEffect(() => {
    load();
  }, [load]);

  // Marca todas las propias como leídas al entrar (los globales con
  // user_id IS NULL no se "leen" porque pertenecen a todos los admins).
  //
  // Y NO TOCA LAS DESCARTADAS. Esta página SÍ pinta el histórico —el checkbox
  // «Mostrar descartadas» de abajo—, así que sin `dismissed=false` enseñaba un
  // dato que su propia visita destruía: entras, ves una descartada «no leída»,
  // vuelves y ya está leída. Medido en producción: 210 filas de 43 personas
  // están `dismissed AND NOT read`. El gemelo de la app tenía la misma línea.
  useEffect(() => {
    if (!profile) return;
    // No hay estado local que dependa de esto, pero el error sí se cuenta:
    // si no entra, las notificaciones reaparecerán como no leídas y el admin
    // merece saber por qué.
    void supabase
      .from('notifications')
      .update({ read: true })
      .eq('user_id', profile.id)
      .eq('read', false)
      .eq('dismissed', false)
      .then(({ error }) => {
        if (error) setError(`No se pudieron marcar como leídas: ${error.message}`);
      });
  }, [profile]);

  async function handleDismiss(id: string) {
    const { error: rpcErr } = await supabase.rpc('dismiss_notification', { p_id: id });
    if (rpcErr) {
      setError(rpcErr.message);
      return;
    }
    // Optimista: marca como descartado en local. Refrescar si el
    // usuario tiene el filtro a "ocultar descartadas".
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, dismissed: true } : n)));
  }

  const visible = useMemo(
    () => (showDismissed ? items : items.filter((n) => !n.dismissed)),
    [items, showDismissed]
  );

  const unreadCount = useMemo(
    () => items.filter((n) => !n.read && !n.dismissed && n.user_id === profile?.id).length,
    [items, profile?.id]
  );

  return (
    <div className="space-y-6">
      <div className="card" data-testid="notifs-page">
        <div className="flex items-center gap-3 mb-6">
          <Bell size={20} className="text-gold-primary" />
          <h2 className="font-oswald text-2xl uppercase tracking-wider text-white">
            Notificaciones
          </h2>
          {unreadCount > 0 && (
            <span className="badge-gold ml-auto" data-testid="notifs-unread-count">
              {unreadCount} nuevas
            </span>
          )}
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-error/10 border border-error/30 rounded-lg p-3 mb-4">
            <AlertCircle size={16} className="text-error flex-shrink-0" />
            <p className="text-error text-sm font-inter" data-testid="notifs-error">
              {error}
            </p>
          </div>
        )}

        <div className="flex items-center justify-between mb-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showDismissed}
              onChange={(e) => setShowDismissed(e.target.checked)}
              className="w-4 h-4 accent-gold-primary"
              data-testid="notifs-show-dismissed"
            />
            <span className="text-text-secondary text-sm font-inter">Mostrar descartadas</span>
          </label>
          <button
            onClick={() => void load()}
            className="text-text-secondary hover:text-gold-primary text-sm font-inter"
            data-testid="notifs-refresh"
          >
            Refrescar
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-gold-primary/30 border-t-gold-primary rounded-full animate-spin" />
          </div>
        ) : cargaFallida ? null : visible.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center text-center py-12 text-text-secondary"
            data-testid="notifs-empty"
          >
            <Inbox size={32} className="mb-2 opacity-60" />
            <p className="font-inter text-sm">
              {showDismissed ? 'No hay notificaciones' : 'No hay notificaciones activas'}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-bg-border" data-testid="notifs-list">
            {visible.map((n) => (
              <li
                key={n.id}
                className={`py-4 flex items-start gap-3 ${n.dismissed ? 'opacity-50' : ''}`}
                data-testid={`notif-item-${n.id}`}
              >
                <div
                  className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${n.read || n.dismissed ? 'bg-bg-border' : 'bg-gold-primary'}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-white font-inter text-sm font-medium">{n.title}</p>
                    <p className="text-text-secondary text-xs whitespace-nowrap">
                      {formatWhen(n.created_at)}
                    </p>
                  </div>
                  <p className="text-text-secondary text-sm font-inter mt-1 whitespace-pre-wrap break-words">
                    {n.body}
                  </p>
                  {n.type && (
                    <span className="inline-block mt-2 text-xs text-text-secondary opacity-70 uppercase tracking-wide">
                      {n.type}
                    </span>
                  )}
                </div>
                {!n.dismissed && (
                  <button
                    onClick={() => void handleDismiss(n.id)}
                    className="text-text-secondary hover:text-error transition-colors mt-1 flex-shrink-0"
                    title="Descartar"
                    data-testid={`notif-dismiss-${n.id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
                {n.dismissed && (
                  <span
                    className="text-success text-xs flex items-center gap-1 mt-1 flex-shrink-0"
                    data-testid={`notif-dismissed-${n.id}`}
                  >
                    <Check size={14} /> Descartada
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}

        {truncado && (
          <p
            className="mt-4 text-text-secondary text-xs font-inter text-center border border-bg-border rounded-lg p-3"
            data-testid="notifs-truncado"
          >
            Hay más de {TOPE} notificaciones y aquí solo caben las {TOPE} más recientes. Las
            anteriores no se han borrado: no están en esta página.
          </p>
        )}
      </div>
    </div>
  );
}
